/**
 * \u6781\u8f7b\u91cf\u8865\u95f4\u5f15\u64ce \u2014\u2014 \u96f6\u4f9d\u8d56\uff0c\u534e\u4e3a\u65f6\u95f4\u8f74\u670d\u52a1\u3002
 *
 * \u8bbe\u8ba1\u53d6\u820d\uff1a
 *   - \u4e0d\u505a\u5b8c\u6574\u7684 Tween.js \u514b\u9686\uff0c\u53ea\u505a\u5e8f\u5217\u5f0f\u65f6\u95f4\u8f74\uff08timeline\uff09\u6240\u9700\u7684\u6700\u5c0f\u96c6
 *   - \u65f6\u95f4\u5355\u4f4d\u7edf\u4e00\u7528\u6beb\u79d2
 *   - \u652f\u6301 parallel group\uff08\u540c\u65f6\u6267\u884c\u591a\u4e2a\u8865\u95f4\uff09\u4e0e sequence\uff08\u4e32\u884c\uff09
 */

export type EasingFn = (t: number) => number;

// \u2500\u2500\u2500\u2500 \u7f13\u52a8\u51fd\u6570 \u2500\u2500\u2500\u2500

export const Easing = {
  linear: (t: number): number => t,

  /** \u843d\u4f53\u611f\uff1a\u8d8a\u5230\u540e\u671f\u8d8a\u6162\uff08\u91cd\u529b\u843d\u4f4d\u4e3b\u529b\uff09 */
  easeOutCubic: (t: number): number => 1 - Math.pow(1 - t, 3),
  easeOutQuart: (t: number): number => 1 - Math.pow(1 - t, 4),
  easeOutBack: (t: number): number => {
    const c1 = 1.70158, c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  easeOutElastic: (t: number): number => {
    if (t === 0 || t === 1) return t;
    const c4 = (2 * Math.PI) / 3;
    return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  },
  easeInQuad: (t: number): number => t * t,
  easeInOutCubic: (t: number): number =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  easeOutQuad: (t: number): number => 1 - (1 - t) * (1 - t),
} as const;

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

// \u2500\u2500\u2500\u2500 \u52a8\u4f5c \u2500\u2500\u2500\u2500

export interface Action {
  /** \u603b\u65f6\u957f\uff08\u6beb\u79d2\uff09 */
  duration: number;
  /** \u6bcf\u5e27\u56de\u8c03\uff08t \u5df2\u7ecf\u8fc7 easing \u5904\u7406\uff0c0..1\uff09 */
  onUpdate(t: number): void;
  /** \u7ed3\u675f\u56de\u8c03\uff08\u4ec5\u5728\u6b63\u5e38\u8d70\u5b8c\u65f6\u89e6\u53d1\uff09 */
  onComplete?(): void;
}

export function createAction(
  duration: number,
  onUpdate: (t: number) => void,
  easing: EasingFn = Easing.easeOutCubic,
  onComplete?: () => void,
): Action {
  return {
    duration,
    onUpdate: (t: number) => onUpdate(easing(clamp01(t))),
    onComplete,
  };
}

/** \u7eaf\u7b49\u5f85\uff08\u8282\u594f\u95f4\u9694\uff09 */
export function wait(duration: number, onComplete?: () => void): Action {
  return { duration, onUpdate: () => {}, onComplete };
}

/** \u7acb\u5373\u6267\u884c\u7684\u96f6\u65f6\u957f\u52a8\u4f5c */
export function instant(fn: () => void): Action {
  return { duration: 0, onUpdate: () => {}, onComplete: fn };
}

// \u2500\u2500\u2500\u2500 \u65f6\u95f4\u8f74 \u2500\u2500\u2500\u2500

/**
 * \u4e00\u4e2a\u987a\u5e8f\u65f6\u95f4\u8f74\uff1a\u4e00\u6b65\u4e00\u6b65\u5730\u64ad\u653e\u52a8\u4f5c\uff0c
 * \u6bcf\u4e00\u6b65\u53ef\u4ee5\u662f\u5355\u4e2a\u52a8\u4f5c\uff0c\u4e5f\u53ef\u4ee5\u662f\u4e00\u7ec4\u5e76\u884c\u52a8\u4f5c\u3002
 */
export class Timeline {
  private steps: Action[][] = [];
  private stepIndex = 0;
  private elapsed = 0;
  private done = false;

  /** \u63a8\u5165\u4e00\u7ec4\u3010\u5e76\u884c\u3011\u52a8\u4f5c\uff08\u5f53\u524d\u7ec4\u5168\u90e8\u8d70\u5b8c\u624d\u8fdb\u5165\u4e0b\u4e00\u7ec4\uff09 */
  parallel(...actions: Action[]): this {
    const flat: Action[] = [];
    for (const a of actions) if (a) flat.push(a);
    if (flat.length > 0) this.steps.push(flat);
    return this;
  }

  /** \u63a8\u5165\u4e00\u4e2a\u3010\u5355\u72ec\u3011\u52a8\u4f5c */
  then(action: Action): this {
    if (action) this.steps.push([action]);
    return this;
  }

  /** \u63a8\u5165\u4e00\u6b65\u7eaf\u7b49\u5f85 */
  delay(ms: number): this {
    return this.then(wait(ms));
  }

  get isDone(): boolean { return this.done; }

  /** \u5f53\u524d\u7ec4\u7684\u603b\u65f6\u957f */
  private currentDuration(): number {
    const group = this.steps[this.stepIndex];
    if (!group) return 0;
    let max = 0;
    for (const a of group) if (a.duration > max) max = a.duration;
    return max;
  }

  /** \u9ad8\u7cbe\u5ea6 tick\uff1adt \u4e3a\u6beb\u79d2 */
  tick(dt: number): void {
    if (this.done) return;
    let remaining = dt;

    // \u9632\u5fa1\uff1adt \u8fc7\u5927\uff08\u5207\u6807\u7b7e\u56de\u6765\uff09\u5219\u88c1\u526a
    if (remaining > 200) remaining = 200;

    while (remaining > 0 && !this.done) {
      const group = this.steps[this.stepIndex];
      if (!group) { this.done = true; return; }

      const dur = this.currentDuration();
      if (dur <= 0) {
        // \u96f6\u65f6\u957f\u7ec4\uff1a\u7acb\u5373\u8d70\u5b8c
        for (const a of group) { a.onUpdate(0); a.onComplete?.(); }
        this.stepIndex++;
        this.elapsed = 0;
        continue;
      }

      const stepRemaining = dur - this.elapsed;
      const consume = Math.min(remaining, stepRemaining);
      this.elapsed += consume;
      remaining -= consume;

      const t = clamp01(this.elapsed / dur);
      for (const a of group) {
        // \u77ed\u52a8\u4f5c\u63d0\u524d\u8dd1\u5230 1\uff0c\u907f\u514d\u5b83\u4eec\u5361\u5728\u4e2d\u95f4
        a.onUpdate(a.duration >= dur ? t : clamp01(this.elapsed / Math.max(a.duration, 1)));
      }

      if (this.elapsed >= dur - 1e-6) {
        for (const a of group) { a.onUpdate(1); a.onComplete?.(); }
        this.stepIndex++;
        this.elapsed = 0;
      }
    }
  }

  /** \u7acb\u5373\u8df3\u5230\u7ed3\u5c3e\uff08\u7528\u4e8e\u300c\u51cf\u5c11\u52a8\u6548\u300d\u6216\u65ad\u5f00\uff09 */
  finish(): void {
    while (!this.done) {
      const group = this.steps[this.stepIndex];
      if (!group) { this.done = true; return; }
      for (const a of group) { a.onUpdate(1); a.onComplete?.(); }
      this.stepIndex++;
      this.elapsed = 0;
    }
  }
}
